// Platform-tier user (verso_admin) CRUD. Distinct from the org dashboard's
// UserManagement, which is org-scoped. The list filters to verso_admin +
// deactivated ex-admins only.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext.jsx';
import { api } from '../lib/api.js';
import ActionMenu from '../components/ActionMenu.jsx';

function CreateAdminModal({ onClose, onSubmit, isPending, error }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">Create Verso Admin</h3>
        <form
          onSubmit={(e) => { e.preventDefault(); onSubmit({ name, email }); }}
          className="mt-4 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoFocus
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Jane Smith" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="jane@colorado-communications.com" />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={isPending}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmDeactivateDialog({ userName, onConfirm, onCancel, isPending }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">Deactivate Admin</h3>
        <p className="mt-2 text-sm text-gray-600">
          Deactivate <span className="font-semibold">{userName}</span>? They will lose all platform access immediately.
        </p>
        <div className="mt-4 flex justify-end gap-3">
          <button onClick={onCancel} className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={onConfirm} disabled={isPending}
            className="rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
            {isPending ? 'Deactivating...' : 'Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_COLORS = {
  verso_admin: 'bg-green-100 text-green-700',
  deactivated: 'bg-red-100 text-red-600',
};

export default function PlatformUsers() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [deactivatingUser, setDeactivatingUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', reactivate: false });

  const { data, isLoading } = useQuery({
    queryKey: ['platform-users'],
    queryFn: () => api.get('/verso-admin/users'),
  });

  const createMutation = useMutation({
    mutationFn: (body) => api.post('/verso-admin/users', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-users'] });
      setShowCreate(false);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (userId) => api.delete(`/verso-admin/users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-users'] });
      setDeactivatingUser(null);
    },
  });

  // Edit name/email; setting role back to verso_admin is the reactivation
  // path for a deactivated platform admin. Changing the email clears the CF
  // Access binding so the account re-attaches on next login.
  const editMutation = useMutation({
    mutationFn: ({ userId, body }) => api.put(`/verso-admin/users/${userId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-users'] });
      setEditingUser(null);
    },
  });

  const allUsers = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
  // Platform admins + ex-admins (deactivated verso_admins). Org-scoped users
  // are managed on the org dashboard, not here.
  const platformAdmins = allUsers.filter((u) => u.role === 'verso_admin' || u.role === 'deactivated');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Users</h1>
          {!isLoading && <p className="mt-1 text-sm text-gray-500">{platformAdmins.length} administrator{platformAdmins.length !== 1 ? 's' : ''}</p>}
        </div>
        <button onClick={() => setShowCreate(true)}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
          Create Verso Admin
        </button>
      </div>

      {showCreate && (
        <CreateAdminModal
          onClose={() => { setShowCreate(false); createMutation.reset(); }}
          onSubmit={(body) => createMutation.mutate(body)}
          isPending={createMutation.isPending}
          error={createMutation.isError ? (createMutation.error?.body?.error || createMutation.error?.message) : null}
        />
      )}

      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">Edit Platform User</h3>
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs text-gray-500">Name</label>
                <input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500">Email</label>
                <input type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
                {editForm.email.trim().toLowerCase() !== (editingUser.email || '').toLowerCase() && (
                  <p className="mt-1 text-xs text-amber-700">
                    Changing the email re-binds sign-in: they log in with the new address next time.
                  </p>
                )}
              </div>
              {editingUser.role === 'deactivated' && (
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={editForm.reactivate}
                    onChange={(e) => setEditForm((f) => ({ ...f, reactivate: e.target.checked }))} className="mt-0.5" />
                  <span>
                    Reactivate as Verso Admin
                    <span className="block text-xs text-gray-400">Restores platform-admin access immediately.</span>
                  </span>
                </label>
              )}
            </div>
            {editMutation.isError && (
              <p className="mt-3 text-sm text-red-600">{editMutation.error?.body?.error || editMutation.error?.message}</p>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => { setEditingUser(null); editMutation.reset(); }}
                className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button
                onClick={() => {
                  const body = { name: editForm.name, email: editForm.email };
                  if (editingUser.role === 'deactivated' && editForm.reactivate) body.role = 'verso_admin';
                  editMutation.mutate({ userId: editingUser.user_id, body });
                }}
                disabled={editMutation.isPending || !editForm.name.trim() || !editForm.email.includes('@')}
                className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                {editMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deactivatingUser && (
        <ConfirmDeactivateDialog
          userName={deactivatingUser.name}
          onConfirm={() => deactivateMutation.mutate(deactivatingUser.user_id)}
          onCancel={() => setDeactivatingUser(null)}
          isPending={deactivateMutation.isPending}
        />
      )}

      {isLoading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">Loading...</div>
      ) : platformAdmins.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
          <div className="text-4xl">⊕</div>
          <h2 className="mt-4 text-lg font-semibold text-gray-900">No platform administrators</h2>
          <p className="mt-2 text-sm text-gray-500">Create the first Verso Admin to get started.</p>
          <button onClick={() => setShowCreate(true)}
            className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
            Create Verso Admin
          </button>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Last Login</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {platformAdmins.map((u) => {
                const isSelf = u.user_id === currentUser?.user_id;
                return (
                  <tr key={u.user_id}>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[u.role] || 'bg-gray-100 text-gray-500'}`}>
                        {u.role === 'deactivated' ? 'Deactivated' : 'Active'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {u.name}
                      {isSelf && <span className="ml-2 text-xs text-gray-400">(you)</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{u.email}</td>
                    <td className="px-4 py-3 text-gray-500">{u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}</td>
                    <td className="px-4 py-3 text-right">
                      <ActionMenu
                        title={`Actions for ${u.name}`}
                        items={[
                          {
                            label: 'Edit user',
                            onClick: () => {
                              setEditingUser(u);
                              setEditForm({ name: u.name || '', email: u.email || '', reactivate: false });
                              editMutation.reset();
                            },
                          },
                          ...(u.role !== 'deactivated' ? [
                            { divider: true },
                            {
                              // Self-deactivation is a lockout — the item stays
                              // visible but disabled so the menu shape matches
                              // every other row.
                              label: isSelf ? 'Deactivate (you)' : 'Deactivate',
                              danger: true,
                              disabled: isSelf,
                              onClick: () => setDeactivatingUser(u),
                            },
                          ] : []),
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
